import { useMemo } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
    Cell,
} from 'recharts';
import { INSIGHTS } from '@/constants/testIds';

const MUSCLE_PALETTE = {
    chest: '#00FF9D',
    back: '#7CFFC4',
    legs: '#FF4D4D',
    shoulders: '#FFB347',
    arms: '#A78BFA',
    core: '#60A5FA',
    cardio: '#FBBF24',
    biceps: '#A78BFA',
    triceps: '#A78BFA',
    quads: '#FF4D4D',
    hamstrings: '#FF6B6B',
    glutes: '#FF8C8C',
    calves: '#FFB347',
    forearms: '#A78BFA',
};

function colorFor(muscle) {
    return MUSCLE_PALETTE[muscle] || '#94A3B8';
}

function TooltipChip({ active, payload, units }) {
    if (!active || !payload?.length) return null;
    const row = payload[0].payload;
    return (
        <div className="glass-card rounded-xl px-3 py-2">
            <p className="uppercase-mono text-muted-foreground mb-1">
                {row.muscle}
            </p>
            <p className="font-mono text-base font-bold tabular-nums">
                {Math.round(row.value).toLocaleString()} {units}
            </p>
            {row.prevValue > 0 && (
                <p className="font-mono text-xs text-muted-foreground mt-0.5">
                    prev wk · {Math.round(row.prevValue).toLocaleString()}
                </p>
            )}
        </div>
    );
}

/**
 * Per-muscle volume for the most recent week (with previous-week baseline
 * shown as a translucent reference bar behind each main bar).
 */
export function MuscleVolumeChart({ weeks = [], units = 'kg', isDark }) {
    const data = useMemo(() => {
        if (!weeks?.length) return [];
        const current = weeks[weeks.length - 1];
        const prev = weeks.length > 1 ? weeks[weeks.length - 2] : null;
        const currentMap = current?.by_muscle || {};
        const prevMap = prev?.by_muscle || {};
        const muscles = Array.from(
            new Set([
                ...Object.keys(currentMap),
                ...Object.keys(prevMap),
            ])
        );
        return muscles
            .map((m) => ({
                muscle: m,
                value: currentMap[m] || 0,
                prevValue: prevMap[m] || 0,
                fill: colorFor(m),
            }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 8);
    }, [weeks]);

    if (!data.length) {
        return (
            <p
                data-testid={INSIGHTS.muscleVolumeEmpty}
                className="text-sm text-muted-foreground"
            >
                No volume logged this week yet.
            </p>
        );
    }

    const gridColor = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';
    const tickFill = isDark ? '#A0A0A0' : '#666666';

    return (
        <div
            data-testid={INSIGHTS.muscleVolumeChart}
            className="w-full h-[260px] sm:h-[300px]"
        >
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                <BarChart
                    data={data}
                    layout="vertical"
                    margin={{ top: 8, right: 24, left: 4, bottom: 4 }}
                >
                    <CartesianGrid
                        strokeDasharray="2 4"
                        horizontal={false}
                        stroke={gridColor}
                    />
                    <XAxis
                        type="number"
                        stroke={tickFill}
                        tick={{ fill: tickFill, fontSize: 11, fontFamily: 'JetBrains Mono' }}
                        tickLine={false}
                        axisLine={false}
                    />
                    <YAxis
                        type="category"
                        dataKey="muscle"
                        stroke={tickFill}
                        tick={{ fill: tickFill, fontSize: 11, fontFamily: 'JetBrains Mono' }}
                        tickLine={false}
                        axisLine={false}
                        width={80}
                    />
                    <Tooltip
                        cursor={{ fill: gridColor }}
                        content={<TooltipChip units={units} />}
                    />
                    <Bar
                        dataKey="prevValue"
                        radius={[6, 6, 6, 6]}
                        background={{ fill: 'transparent' }}
                        fillOpacity={0.15}
                    >
                        {data.map((d) => (
                            <Cell key={`prev-${d.muscle}`} fill={d.fill} />
                        ))}
                    </Bar>
                    <Bar
                        dataKey="value"
                        radius={[6, 6, 6, 6]}
                    >
                        {data.map((d) => (
                            <Cell key={`cur-${d.muscle}`} fill={d.fill} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}
